import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import UploadResume from '../components/UploadResume';
import { BACKEND_URL } from '../config/api';
import './UploadPage.css';

export default function UploadPage() {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const hasFetchedResumes = useRef(false);

  // Listen for auth changes to refresh user data
  useEffect(() => {
    const handleAuthChange = () => {
      // Refresh credits display when auth state changes
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
      
      // Refresh page data when auth state changes
      if (isAuthenticated() && hasFetchedResumes.current) {
        // Re-fetch resumes if auth was updated
        hasFetchedResumes.current = false;
      }
    };
    
    window.addEventListener('authChanged', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    
    return () => {
      window.removeEventListener('authChanged', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    // Guard against double invocation in React StrictMode/dev
    if (hasFetchedResumes.current) return;
    hasFetchedResumes.current = true;

    // CRITICAL: Handle production hydration timing
    // Check auth immediately, but also recheck after brief delay for production builds
    if (!isAuthenticated()) {
      const recheckTimeout = setTimeout(() => {
        if (!isAuthenticated()) {
          navigate('/signup');
        } else {
          // Auth became valid after hydration, fetch resumes
          fetchResumesData();
        }
      }, 100);
      return () => clearTimeout(recheckTimeout);
    }

    fetchResumesData();
    
    // Fetch resumes from backend API
    async function fetchResumesData() {
      try {
        setLoading(true);
        const headers = getAuthHeaders();
        
        const res = await fetch(`${BACKEND_URL}/api/resumes`, {
          method: 'GET',
          credentials: 'include',
          headers
        });
        if (res.ok) {
          const data = await res.json();
          // Map backend resumes to local state
          const resumesArr = Array.isArray(data.resumes) ? data.resumes : [];
          setResumes(resumesArr.map(r => ({
            id: r.id,
            title: r.filename,
            filename: r.filename,
            url: r.url,
            createdAt: r.createdAt,
            // Add any other fields needed for display
          })));
        } else {
          setResumes([]);
        }
      } catch (e) {
        setResumes([]);
      } finally {
        setLoading(false);
      }
    }
  }, [navigate]);

  const handleUploadSuccess = useCallback((uploadedInfo) => {
    // Always use the backend 'id' for deletion, never 'fileId'
    const newResume = {
      id: uploadedInfo.id, // Only use 'id' from backend
      title: uploadedInfo.filename || 'Untitled Resume',
      filename: uploadedInfo.filename,
      storedName: uploadedInfo.storedName,
      url: uploadedInfo.url,
      // Persist OpenAI file metadata for later processing
      openAiFileId: uploadedInfo?.openAi?.id || null,
      openAiStatus: uploadedInfo?.openAi?.status || null,
      openAiBytes: uploadedInfo?.openAi?.bytes || null,
      createdAt: new Date().toLocaleString(),
    };
    
    const updated = [...resumes, newResume];
    setResumes(updated);
    setShowUploadModal(false);

    // Check if we should return to trial modal
    const trialModalState = localStorage.getItem('trial_modal_state');
    if (trialModalState) {
      try {
        const state = JSON.parse(trialModalState);
        if (state.shouldReopen) {
          // Clear the state
          localStorage.removeItem('trial_modal_state');
          // Navigate back to sessions page which will reopen the modal
          navigate('/sessions', { state: { reopenTrialModal: true, trialModalData: state } });
        }
      } catch (e) {
        console.error('Error parsing trial modal state:', e);
      }
    }
  }, [resumes, navigate]);

  const handleDownload = async (resume) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/resumes/download?resumeId=${resume.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders({ 'Accept': 'application/pdf,application/octet-stream' }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resume.filename || resume.title || 'resume.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
      alert('Failed to download resume. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!id || isNaN(Number(id))) {
      alert('Invalid resume id. Cannot delete.');
      return;
    }
    try {
      const headers = getAuthHeaders();
      
      const res = await fetch(`${BACKEND_URL}/api/resumes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });
      if (!res.ok) {
        throw new Error('Failed to delete resume from backend');
      }
      // Only remove from local state if backend delete succeeded
      const updated = resumes.filter(r => r.id !== id);
      setResumes(updated);
    } catch (e) {
      console.error('Failed to delete resume from backend:', e);
      alert('Failed to delete resume from server. Please try again.');
    }
  };

  return (
    <div className="upload-page-container">
      <div className="upload-page-header">
        <h1>CVs / Resumes</h1>
        <button className="btn-upload-header" onClick={() => setShowUploadModal(true)}>
          Upload Resume
        </button>
      </div>

      <div className="resumes-table-wrapper">
        <table className="resumes-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="empty-state">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px' }}>
                    <div className="spinner" style={{
                      border: '3px solid rgba(255, 255, 255, 0.1)',
                      borderTop: '3px solid #10b981',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#9ca3af' }}>Loading resumes...</span>
                  </div>
                </td>
              </tr>
            ) : resumes.length === 0 ? (
              <tr>
                <td colSpan="3" className="empty-state">
                  No Resumes yet.
                </td>
              </tr>
            ) : (
              resumes.map((resume) => (
                <tr key={resume.id}>
                  <td>{resume.title}</td>
                  <td>{resume.createdAt}</td>
                  <td>
                    <div className="table-actions">
                      {resume.url && (
                        <a href={resume.url} target="_blank" rel="noreferrer" className="btn-view">
                          View
                        </a>
                      )}
                      <button className="btn-download" onClick={() => handleDownload(resume)}>
                        Download
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(resume.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Resume</h2>
              <button className="btn-close" onClick={() => setShowUploadModal(false)}>
                ✕
              </button>
            </div>
            <UploadResume onUploadSuccess={handleUploadSuccess} />
          </div>
        </div>
      )}
    </div>
  );
}
